import { useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useProfile } from '@/api/profile'
import { api } from '@/lib/api'
import { runStaggered } from '@/lib/idleTask'

const ONE_MIN = 60_000
const TWO_MIN = 2 * 60_000

/**
 * Prefetch des queries de l'espace softphone (Dashboard).
 *
 * Concerne aussi bien les Agents que les Managers — un Manager peut cliquer
 * "Mon téléphone" et se retrouver dans cet espace. Donc on prefetch dès le
 * mount de DashboardPage, peu importe le rôle.
 */
export function AgentBootProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { data: profile } = useProfile()
  const companyId = profile?.company

  useEffect(() => {
    if (!profile) return

    // Cascade : 4 prefetch en lots de 2 toutes les 300ms, après 2s d'attente.
    // Étale la charge sur le worker uvicorn unique.
    return runStaggered([
      // Contacts (non archivés) — utilisé partout (Boîte de réception, Contacts).
      // Plus de préchargement de l'ancienne liste de textos : la Boîte de
      // réception l'a remplacée (et sa clé ne correspondait à aucune requête).
      () => {
        queryClient.prefetchQuery({
          queryKey: ['contacts', false],
          queryFn: async () => {
            const { data } = await api.get<{ contacts?: unknown[] }>('/api/contacts/')
            return data.contacts || []
          },
          staleTime: TWO_MIN,
        })
      },
      // Voicemails — utilisé dans CallHistory + VoicemailMiniPlayer
      ...(companyId
        ? [
            () => {
              queryClient.prefetchQuery({
                queryKey: ['voicemails', companyId],
                queryFn: async () => {
                  const { data } = await api.get<{ voicemails?: unknown[] }>(
                    `/api/companies/${companyId}/voicemails/`,
                  )
                  return data.voicemails || []
                },
                staleTime: ONE_MIN,
              })
            },
          ]
        : []),
      // Available caller IDs — sélecteur de ligne PhoneTab
      () => {
        queryClient.prefetchQuery({
          queryKey: ['available-caller-ids'],
          queryFn: async () => {
            const { data } = await api.get<{ numbers?: unknown[] }>(
              '/api/agents/available-numbers/',
            )
            return data.numbers || []
          },
          staleTime: TWO_MIN,
        })
      },
      // Agent status (DND on/off) — extrait data.status (string)
      () => {
        queryClient.prefetchQuery({
          queryKey: ['agent-status'],
          queryFn: async () => {
            const { data } = await api.get<{ status: string }>(
              '/api/agents/status/get/',
            )
            return data.status
          },
          staleTime: ONE_MIN,
        })
      },
    ])
  }, [profile, companyId, queryClient])

  return <>{children}</>
}
